import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, idr, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Bot, Sparkles, Trash2, FileText, Send, Globe, ExternalLink } from "lucide-react";

const AI_BG = "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhdGElMjBuZXR3b3JrfGVufDB8fHx8MTc4NzIzODU2M3ww&ixlib=rb-4.1.0&q=85";

const emptyAddress = () => ({ street: "", city: "", state: "", zip: "", country: "Indonesia" });

export default function AIAssistant() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const requestId = params.get("request");
  const [query, setQuery] = useState("");
  const [realtime, setRealtime] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState("");
  const [sources, setSources] = useState([]);
  const [cust, setCust] = useState({ customer_name: "", customer_email: "", customer_company: "", customer_phone: "", customer_address: emptyAddress(), attention: "", subject: "", tax_percent: 11, notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!requestId) return;
    api.get(`/quotations/${requestId}`).then(({ data }) => {
      setCust((c) => ({
        ...c, customer_name: data.customer_name || "", customer_email: data.customer_email || "",
        customer_company: data.customer_company || "", attention: data.attention || "",
        customer_phone: data.customer_phone || "",
        customer_address: { ...emptyAddress(), ...(data.customer_address || {}) },
        subject: data.subject || "", notes: "",
      }));
      setQuery(data.notes || "");
    }).catch(() => {});
  }, [requestId]);

  const setAddress = (k, v) => setCust((c) => ({ ...c, customer_address: { ...c.customer_address, [k]: v } }));

  const recompute = (it) => {
    const unit = Number(it.purchase_price) * (1 + Number(it.margin_percent) / 100);
    return { ...it, unit_price: Math.round(unit), line_total: Math.round(unit * Number(it.quantity)) };
  };

  const ask = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/ai/recommend", { query, realtime });
      setItems(data.items.map(recompute));
      setSummary(data.summary);
      setSources(data.sources || []);
      toast.success(data.realtime ? "AI + harga realtime (web) selesai" : "AI menyusun rekomendasi harga");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const updateItem = (i, field, val) => {
    setItems((prev) => prev.map((it, idx) => (idx === i ? recompute({ ...it, [field]: val }) : it)));
  };
  const removeItem = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  const subtotal = items.reduce((s, i) => s + i.line_total, 0);
  const tax = Math.round((subtotal * Number(cust.tax_percent)) / 100);
  const total = subtotal + tax;

  const createQuote = async () => {
    if (!cust.customer_name.trim() && !cust.customer_company.trim()) { toast.error("Isi Kepada (Perusahaan) atau Nama Kontak"); return; }
    if (items.length === 0) { toast.error("Belum ada item"); return; }
    setSaving(true);
    try {
      const body = {
        ...cust, customer_name: cust.customer_name.trim() || cust.customer_company.trim(),
        tax_percent: Number(cust.tax_percent),
        items: items.map((i) => ({
          product_name: i.product_name, series: i.series, category: i.category,
          quantity: Number(i.quantity), unit: i.unit || "Unit", purchase_price: Number(i.purchase_price),
          margin_percent: Number(i.margin_percent),
        })),
      };
      let target;
      if (requestId) {
        await api.put(`/quotations/${requestId}`, body);
        const { data } = await api.post(`/quotations/${requestId}/promote`);
        target = data;
      } else {
        const { data } = await api.post("/quotations", body);
        target = data;
      }
      toast.success(`Penawaran ${target.quote_number} siap`);
      navigate(`/quotations/${target.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="ai-page">
      <PageHeader overline={requestId ? "Menjawab Permintaan" : "Otomasi Harga"} title="AI Assistant" />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2">
          <div className="border border-border bg-card rounded-sm relative overflow-hidden">
            <img src={AI_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-[0.06]" />
            <div className="relative p-6">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 bg-accent flex items-center justify-center rounded-sm">
                  <Bot className="w-4 h-4 text-accent-foreground" />
                </div>
                <span className="font-heading font-bold">AI Admin Negosiator</span>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Ketik permintaan customer. AI mencari harga dari katalog / estimasi pasar dan menerapkan margin profit Anda.
              </p>
              <Textarea
                data-testid="ai-query-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Contoh: 5 unit laptop Lenovo ThinkPad X1 Carbon Gen 11 dan 1 switch Cisco Catalyst 2960"
                className="rounded-sm bg-secondary border-border min-h-[110px] font-mono text-sm"
              />
              <div className="flex items-center justify-between mt-3 p-3 border border-border rounded-sm bg-background/50">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-primary" />
                  <div>
                    <div className="text-sm font-medium">Harga Pasar Realtime</div>
                    <div className="text-xs text-muted-foreground">Cari harga terbaru dari internet</div>
                  </div>
                </div>
                <Switch data-testid="realtime-toggle" checked={realtime} onCheckedChange={setRealtime} />
              </div>
              <Button data-testid="ai-ask-btn" onClick={ask} disabled={loading} className="w-full mt-3 rounded-sm bg-accent text-accent-foreground hover:opacity-90">
                <Sparkles className="w-4 h-4 mr-2" />
                {loading ? "AI berpikir…" : "Carikan Harga & Margin"}
              </Button>
              {summary && (
                <div className="mt-4 p-3 border border-border rounded-sm bg-background/60 text-sm text-muted-foreground font-mono" data-testid="ai-summary">
                  {summary}
                </div>
              )}
              {sources.length > 0 && (
                <div className="mt-3" data-testid="ai-sources">
                  <div className="overline text-muted-foreground mb-1">Sumber Web</div>
                  <div className="space-y-1">
                    {sources.slice(0, 5).map((s, i) => (
                      <a key={i} href={s.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-primary hover:underline truncate">
                        <ExternalLink className="w-3 h-3 shrink-0" /> {s.title || s.url}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="border border-border bg-card rounded-sm mt-4 p-6 space-y-3">
            <div className="overline text-muted-foreground">Data Penawaran (sesuai format GRCom)</div>
            <div><Label className="overline text-muted-foreground">Kepada (Perusahaan)</Label>
              <Input data-testid="cust-company-input" value={cust.customer_company} onChange={(e) => setCust({ ...cust, customer_company: e.target.value })} placeholder="mis. PT Timbang Deli Indonesia" className="mt-1 rounded-sm bg-secondary" /></div>
            <div className="border border-border rounded-sm p-3 space-y-3" data-testid="cust-address-section">
              <div><Label className="overline text-muted-foreground">Alamat</Label>
                <Textarea data-testid="cust-address-street" value={cust.customer_address.street} onChange={(e) => setAddress("street", e.target.value)} placeholder="Jalan, nomor, kelurahan/kecamatan" className="mt-1 rounded-sm bg-secondary min-h-[60px]" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="overline text-muted-foreground">Kota / Kab.</Label>
                  <Input data-testid="cust-address-city" value={cust.customer_address.city} onChange={(e) => setAddress("city", e.target.value)} className="mt-1 rounded-sm bg-secondary" /></div>
                <div><Label className="overline text-muted-foreground">Provinsi</Label>
                  <Input data-testid="cust-address-state" value={cust.customer_address.state} onChange={(e) => setAddress("state", e.target.value)} className="mt-1 rounded-sm bg-secondary" /></div>
                <div><Label className="overline text-muted-foreground">Kode Pos</Label>
                  <Input data-testid="cust-address-zip" value={cust.customer_address.zip} onChange={(e) => setAddress("zip", e.target.value)} className="mt-1 rounded-sm bg-secondary" /></div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="overline text-muted-foreground">Phone</Label>
                <Input data-testid="cust-phone-input" value={cust.customer_phone} onChange={(e) => setCust({ ...cust, customer_phone: e.target.value })} placeholder="(061) 7983xxx" className="mt-1 rounded-sm bg-secondary" /></div>
              <div><Label className="overline text-muted-foreground">Att</Label>
                <Input data-testid="cust-att-input" value={cust.attention} onChange={(e) => setCust({ ...cust, attention: e.target.value })} placeholder="mis. Bp Anggi" className="mt-1 rounded-sm bg-secondary" /></div>
            </div>
            <div><Label className="overline text-muted-foreground">Hal</Label>
              <Input data-testid="cust-subject-input" value={cust.subject} onChange={(e) => setCust({ ...cust, subject: e.target.value })} placeholder="mis. Penawaran Desktop" className="mt-1 rounded-sm bg-secondary" /></div>
            <div className="text-[11px] text-muted-foreground">Ref No dibuat otomatis (GR + bulan + tahun + urutan) saat disimpan.</div>
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
              <div><Label className="overline text-muted-foreground">Nama Kontak</Label>
                <Input data-testid="cust-name-input" value={cust.customer_name} onChange={(e) => setCust({ ...cust, customer_name: e.target.value })} className="mt-1 rounded-sm bg-secondary" /></div>
              <div><Label className="overline text-muted-foreground">Email (untuk kirim penawaran)</Label>
                <Input data-testid="cust-email-input" value={cust.customer_email} onChange={(e) => setCust({ ...cust, customer_email: e.target.value })} className="mt-1 rounded-sm bg-secondary" /></div>
            </div>
            <div><Label className="overline text-muted-foreground">Pajak (%)</Label>
              <Input type="number" value={cust.tax_percent} onChange={(e) => setCust({ ...cust, tax_percent: e.target.value })} className="mt-1 rounded-sm bg-secondary" /></div>
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="border border-border bg-card rounded-sm">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <div className="overline text-muted-foreground">Rekomendasi Penawaran</div>
              <span className="font-mono text-xs text-muted-foreground">{items.length} item</span>
            </div>
            {items.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">
                <FileText className="w-8 h-8 mx-auto mb-3 opacity-40" />
                <p className="text-sm">Belum ada rekomendasi. Mulai dengan bertanya ke AI.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary">
                    <tr className="text-left overline text-muted-foreground">
                      <th className="px-3 py-2">Description</th><th className="px-3 py-2">Unit</th>
                      <th className="px-3 py-2">Harga Beli (internal)</th><th className="px-3 py-2">Margin %</th>
                      <th className="px-3 py-2">Unit Price</th><th className="px-3 py-2">Sub Total (IDR)</th><th></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {items.map((it, i) => (
                      <tr key={i} data-testid={`ai-item-row-${i}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium">{it.product_name}</div>
                          <div className="text-xs text-muted-foreground">{it.series} {it.estimated && <span className="text-accent">· estimasi</span>}</div>
                        </td>
                        <td className="px-3 py-2"><Input type="number" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} className="w-16 h-8 rounded-sm bg-secondary" /></td>
                        <td className="px-3 py-2"><Input type="number" value={it.purchase_price} onChange={(e) => updateItem(i, "purchase_price", e.target.value)} className="w-28 h-8 rounded-sm bg-secondary font-mono text-xs" /></td>
                        <td className="px-3 py-2"><Input type="number" value={it.margin_percent} onChange={(e) => updateItem(i, "margin_percent", e.target.value)} className="w-16 h-8 rounded-sm bg-secondary" data-testid={`ai-margin-${i}`} /></td>
                        <td className="px-3 py-2 font-mono text-xs">{idr(it.unit_price)}</td>
                        <td className="px-3 py-2 font-mono text-xs font-semibold">{idr(it.line_total)}</td>
                        <td className="px-3 py-2"><button onClick={() => removeItem(i)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="p-6 border-t border-border">
                  <div className="ml-auto max-w-xs space-y-1 text-sm">
                    <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span className="font-mono">{idr(subtotal)}</span></div>
                    <div className="flex justify-between text-muted-foreground"><span>PPN</span><span className="font-mono">{idr(tax)}</span></div>
                    <div className="flex justify-between font-heading font-black text-lg pt-2 border-t border-border"><span>Total</span><span className="font-mono">{idr(total)}</span></div>
                    <div className="text-[11px] text-muted-foreground pt-1">*Harga belum termasuk PPN</div>
                  </div>
                  <Button data-testid="create-quote-btn" onClick={createQuote} disabled={saving} className="w-full mt-4 rounded-sm">
                    <Send className="w-4 h-4 mr-2" /> {saving ? "Menyimpan…" : "Konversi ke Form Penawaran"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
